import { useEffect, useRef } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { db, auth } from '../services/firebase';
import { TripData } from '../types';
import { sanitizeForFirestore } from '../utils/appHelpers';

// ── LIVE VAULT SYNC — EXACT-COPY GUARANTEE ──────────────────────────────
// The saved/shared document must always match what the builder sees.
// Background enrichments (venue URL lookups, deep link validation, slow
// skeleton weather/logistics, late-arriving day images) only updated React
// state; if the tab closed before a timed auto-save fired, shared links
// and the vault rendered an older, different itinerary. This effect
// debounces every tripData change during the build session and merges a
// sanitized snapshot into Firestore, so both sides always see the same trip.
// Extracted verbatim from App.tsx.
export const useVaultSync = (
  tripData: TripData | null,
  savedTripId: string | null,
  isPublicView: boolean,
  headerImage: string | undefined,
) => {
  const lastVaultSyncRef = useRef<string>('');
  useEffect(() => {
    if (!db || !auth?.currentUser || !savedTripId || !tripData || isPublicView) return;
    const realDays = (tripData.itinerary || []).filter((d: any) => !d.isPlaceholder);
    if (realDays.length === 0) return;

    const handle = setTimeout(async () => {
      try {
        // Base64 images are excluded (they exceed Firestore's 1MB doc limit);
        // the Storage-upload flows swap them for https URLs in state, which a
        // following sync pass then persists.
        const httpsOnly = (url?: string) => (url && !url.startsWith('data:') ? url : undefined);
        const payload = sanitizeForFirestore({
          itinerary: (tripData.itinerary || []).map((d: any) => {
            const img = httpsOnly(d.imageUrl);
            const { imageUrl, ...rest } = d;
            return img ? { ...rest, imageUrl: img } : rest;
          }),
          ...(tripData.logistics ? { logistics: tripData.logistics } : {}),
          ...(tripData.weather ? { weather: tripData.weather } : {}),
          ...(tripData.packingList ? { packingList: tripData.packingList } : {}),
          ...(tripData.budget ? { budget: tripData.budget } : {}),
          ...(tripData.destinationInsights ? { destinationInsights: tripData.destinationInsights } : {}),
          ...(tripData.destinationSummary ? { destinationSummary: tripData.destinationSummary } : {}),
          ...(tripData.welcomeNote ? { welcomeNote: tripData.welcomeNote } : {}),
          ...(tripData.eSimRecommendation ? { eSimRecommendation: tripData.eSimRecommendation } : {}),
          ...(tripData.localEvents?.length ? { localEvents: tripData.localEvents } : {}),
          ...(httpsOnly(headerImage) ? { headerImageUrl: httpsOnly(headerImage) } : {}),
        });
        const fingerprint = JSON.stringify(payload);
        if (fingerprint === lastVaultSyncRef.current) return;
        await setDoc(doc(db, "itineraries", savedTripId), payload, { merge: true });
        lastVaultSyncRef.current = fingerprint;
      } catch (err) {
        console.warn('[VaultSync] Live sync failed (will retry on next change):', err);
      }
    }, 4000);
    return () => clearTimeout(handle);
  }, [tripData, savedTripId, isPublicView, headerImage]);
};
